import type { GraphDocument } from '@/domain/funnel/graph-document';
import type { EditorEdge, EditorGraph, EditorNode } from '../domain/editor-graph';

const EDITOR_NODE_KEYS = new Set([
  'selected',
  'dragging',
  'measured',
  'draggable',
  'selectable',
  'zIndex',
]);
const EDITOR_EDGE_KEYS = new Set(['selected']);
const EDITOR_DATA_KEYS = new Set(['__editorOriginalType', '__editorLocked']);

function stripEditorDataKeys(data: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!EDITOR_DATA_KEYS.has(key)) clean[key] = value;
  }
  return clean;
}

/**
 * SPEC-007 §16/graph-schema.md §4 — restores the true persisted type for a
 * node/edge the editor rendered through its fallback/unknown path. Without
 * this, saving a graph that contains forward-compatible unknown data would
 * permanently overwrite its real `type` with `fallback`/`unknown`,
 * destroying exactly the compatibility SPEC-005/006 exist to preserve.
 */
function resolvePersistedType(renderedType: string, data: Record<string, unknown>): string {
  const original = data.__editorOriginalType;
  return typeof original === 'string' ? original : renderedType;
}

function serializeNode(node: EditorNode): GraphDocument['nodes'][number] {
  const data = (node.data ?? {}) as Record<string, unknown>;
  const type = resolvePersistedType(node.type ?? 'generic', data);
  const record: Record<string, unknown> = {
    id: node.id,
    type,
    position: { x: node.position.x, y: node.position.y },
    data: stripEditorDataKeys(data),
    locked: Boolean(node.locked),
  };
  if (node.width !== undefined) record.width = node.width;
  if (node.height !== undefined) record.height = node.height;
  for (const [key, value] of Object.entries(node)) {
    if (
      key in record ||
      EDITOR_NODE_KEYS.has(key) ||
      key === 'data' ||
      key === 'type' ||
      key === 'position' ||
      key === 'locked' ||
      key === 'id' ||
      key === 'width' ||
      key === 'height'
    ) {
      continue;
    }
    record[key] = value;
  }
  return record as GraphDocument['nodes'][number];
}

function serializeEdge(edge: EditorEdge): GraphDocument['edges'][number] {
  const data = (edge.data ?? {}) as Record<string, unknown>;
  const type = resolvePersistedType(edge.type ?? 'standard', data);
  const record: Record<string, unknown> = {
    id: edge.id,
    type,
    source: edge.source,
    target: edge.target,
    data: stripEditorDataKeys(data),
  };
  if (edge.sourceHandle) record.sourceHandle = edge.sourceHandle;
  if (edge.targetHandle) record.targetHandle = edge.targetHandle;
  for (const [key, value] of Object.entries(edge)) {
    if (
      key in record ||
      EDITOR_EDGE_KEYS.has(key) ||
      key === 'data' ||
      key === 'type' ||
      key === 'source' ||
      key === 'target' ||
      key === 'sourceHandle' ||
      key === 'targetHandle' ||
      key === 'id'
    ) {
      continue;
    }
    record[key] = value;
  }
  return record as GraphDocument['edges'][number];
}

/**
 * SPEC-007 §16/SAVE-REQ-014/T007 — the single canonical serializer. Persists
 * exactly `schemaVersion`/`nodes`/`edges`/`viewport` plus any registered
 * top-level extension already present on the graph (graph-schema.md §3),
 * and excludes every transient editor-only field (selection, drag/measured
 * runtime state, editor bookkeeping keys) per graph-schema.md §4.
 */
export function serializeEditorGraph(graph: EditorGraph): GraphDocument {
  return {
    ...graph,
    nodes: graph.nodes.map(serializeNode),
    edges: graph.edges.map(serializeEdge),
    viewport: { x: graph.viewport.x, y: graph.viewport.y, zoom: graph.viewport.zoom },
  };
}

export function measureGraphPayloadBytes(graph: GraphDocument): number {
  return new TextEncoder().encode(JSON.stringify(graph)).length;
}
